import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * Shape of GET /territories/{insee}/heat-islands.
 * Aggregated urban-heat-island KPIs for a commune.
 *
 * `_source` flags the provenance of the figures ("real" once the backend
 * computes them from Copernicus LST / Corine Land Cover / INSEE carroyage,
 * "demo" otherwise).
 */
export interface HeatIslandsData {
  territory_code: string;
  /** Max relative urban-heat-island differential (°C, vs. rural baseline). */
  delta_t_max: number;
  /** Share of the territory above the overheating threshold (%). */
  overheated_pct: number;
  /** Population living inside overheated cells (inhabitants). */
  exposed_population: number;
  /** Tree-canopy cover (%). */
  canopy_pct: number;
  _source?: 'real' | 'demo' | string;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Fetches the urban-heat-island KPIs for a given commune (INSEE code).
 * GET /territories/{insee}/heat-islands
 *
 * Standard React Query result (data, isLoading, error, refetch…).
 * Disabled while `insee` is null (no territory selected).
 */
export function useHeatIslands(insee: string | null) {
  return useQuery<HeatIslandsData>({
    queryKey: ['heat-islands', insee],
    queryFn: async () => {
      const { data } = await api.get<HeatIslandsData>(
        `/territories/${insee}/heat-islands`,
      );
      return data;
    },
    enabled: !!insee,
    retry: false,
  });
}
